// v3.0.0-rc.8g — FavoritesNoveltiesStrip : bandeau "Nouveautés chez tes favoris"
// affiché en haut du chat home (uniquement en début de conversation).
// Data : /api/v3/favorites/merchants/updates (déjà en place).
// Se masque automatiquement si non-authentifié ou aucun favori.

import { useCallback, useEffect, useState } from "react";
import { addItem as addToLocalCart } from "@/lib/localCartStore";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function fmtFcfa(n) {
  if (!Number.isFinite(Number(n))) return "";
  return Number(n).toLocaleString("fr-FR") + " FCFA";
}

export default function FavoritesNoveltiesStrip({ onSelectProduct }) {
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const token = typeof window !== "undefined" ? localStorage.getItem("civ_user_token") : "";
    if (!token) { setLoaded(true); return; }
    try {
      const r = await fetch(`${API}/v3/favorites/merchants/updates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { setLoaded(true); return; }
      const b = await r.json();
      setProducts(Array.isArray(b.products) ? b.products : []);
    } catch (_) { /* silent */ }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => {
    load();
    // Reload quand l'utilisateur ajoute/retire un favori.
    const handler = () => load();
    window.addEventListener("aya-favorites-updated", handler);
    return () => window.removeEventListener("aya-favorites-updated", handler);
  }, [load]);

  if (!loaded || products.length === 0) return null;

  return (
    <section
      data-testid="favorites-novelties-strip"
      style={{
        margin: "8px 0 14px",
        padding: "10px 12px 12px",
        borderRadius: 14,
        background: "linear-gradient(135deg, rgba(255,126,27,0.08), rgba(232,213,163,0.05))",
        border: "1px solid rgba(255,126,27,0.22)",
      }}
    >
      <header style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 8,
      }}>
        <div style={{
          fontSize: 12, fontWeight: 800, letterSpacing: 0.4,
          textTransform: "uppercase", color: "#FF7E1B",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <span aria-hidden style={{ fontSize: 15 }}>❤️</span>
          <span>Nouveautés chez tes favoris</span>
        </div>
        <span style={{ fontSize: 11, opacity: 0.55, fontWeight: 600 }}>
          {products.length} {products.length > 1 ? "articles" : "article"}
        </span>
      </header>
      <div
        style={{
          display: "flex", gap: 10, overflowX: "auto",
          padding: "2px 2px 6px",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "thin",
          scrollSnapType: "x proximity",
        }}
      >
        {products.map((p) => (
          <article
            key={p.product_id}
            data-testid={`favnov-item-${p.product_id}`}
            style={{
              flex: "0 0 160px",
              width: 160,
              scrollSnapAlign: "start",
              borderRadius: 12,
              background: "#0B4531",
              color: "#F4F1E8",
              border: "1px solid rgba(232,213,163,0.28)",
              overflow: "hidden",
              display: "flex", flexDirection: "column",
              cursor: onSelectProduct ? "pointer" : "default",
            }}
            onClick={() => onSelectProduct && onSelectProduct(p)}
          >
            <div style={{
              width: "100%", aspectRatio: "1/1",
              background: "#04180F",
              display: "flex", alignItems: "center", justifyContent: "center",
              overflow: "hidden", position: "relative",
            }}>
              {(Array.isArray(p.photos) && p.photos[0])
                ? <img src={p.photos[0]} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />
                : <span style={{ fontSize: 40 }}>{p.emoji || "🛍"}</span>}
              <button
                type="button"
                data-testid={`favnov-add-${p.product_id}`}
                aria-label="Ajouter au panier"
                onClick={(e) => {
                  e.stopPropagation();
                  addToLocalCart({
                    product_id: p.product_id,
                    name: p.name,
                    price_fcfa: p.price_fcfa,
                    merchant_id: p.merchant_id,
                    merchant_name: p.merchant_name,
                    photos: p.photos || [],
                    emoji: p.emoji,
                  });
                }}
                style={{
                  position: "absolute", bottom: 6, right: 6,
                  width: 26, height: 26, borderRadius: "50%",
                  border: "none",
                  background: "linear-gradient(135deg, #F5E9C6, #E4C87F 55%, #CDA452)",
                  color: "#3A2E10",
                  fontWeight: 900, fontSize: 15, lineHeight: 1,
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
                }}
              >＋</button>
            </div>
            <div style={{ padding: "8px 10px 10px" }}>
              <div style={{
                fontSize: 12, fontWeight: 700, lineHeight: 1.25,
                overflow: "hidden", textOverflow: "ellipsis",
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                minHeight: "2.5em",
              }}>{p.name}</div>
              <div style={{
                fontSize: 10.5, opacity: 0.65, marginTop: 3,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{p.merchant_name || "—"}</div>
              <div style={{
                fontSize: 12, fontWeight: 800, color: "#EBD9A8",
                marginTop: 5, fontFeatureSettings: "'tnum'",
              }}>{fmtFcfa(p.price_fcfa)}</div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
